<template>
  <div class="seller-users-tab">
    <div class="users-filters">
      <button :class="['users-filter', { active: !filter }]" @click="filter = null">
        <span class="value">{{ usersList.length }}</span>
        {{ trans("live.user_list.tabs.bidders") }}
      </button>
      <button :class="['users-filter', { active: filter === 'active' }]" @click="filter = 'active'">
        <span class="value">{{ activeCount }}</span>
        {{ trans("live.user_list.tabs.active") }}
      </button>
      <div class="users-filter">
        <span class="value">{{ auctionStore.userOnLiveAnonymous || 0 }}</span>
        {{ trans("live.user_list.tabs.watchers") }}
      </div>
      <div
        v-if="pendingUsers?.length"
        :class="['users-filter pending', { active: filter === 'pending' }]"
        @click="filter = 'pending'">
        <span class="value">!</span>
      </div>
    </div>
    <custom-scrollbar class="custom-scrollbar controller-scrollbar">
      <div class="users-component">
        <!--    Add class .clarified for gray color-->
        <!--    Add class .active for green marker -->
        <div
          v-for="(user, index) in filteredUsersList"
          :class="['user-row', user.status, getUserStatus(user)]"
          :key="index"
          @click="openUserPopup(user)">
          {{ user.full_name }}
          <base-icon
            class="send-message"
            icon-name="mail-live"
            width="22.636"
            height="18.109"
            view-box="0 0 22.636 18.109"
            @click.stop.prevent="openChat(user)">
            <mail-live-icon></mail-live-icon>
          </base-icon>
        </div>
      </div>
    </custom-scrollbar>
    <popup class="user-info-popup" :title="popupTitle" ref="popup"></popup>
  </div>
</template>
<script setup lang="ts">
  import { computed, inject, markRaw, nextTick, ref, watch } from "vue";
  import CustomScrollbar from "@/components/_partials/CustomScrollbar.vue";
  import Popup from "@/components/_partials/Popup.vue";
  import UserInfoPopup from "@/components/_partials/UserInfoPopup.vue";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { IUserListItem } from "@/models/api/user/IUserListItem";
  import { trans } from "@/utils/trans";
  import apiClient from "@/services/api";
  import { CustomerStatus } from "@/enum";
  import { BaseIcon, MailLiveIcon } from "@/components/icons";

  const popup = ref<typeof Popup>();
  const component = markRaw(UserInfoPopup);
  const auctionStore = useAuctionStore();
  const filter = ref<null | "active" | "pending">(null);
  const popupTitle = ref<string | null>(null);
  const emitter = inject("emitter");
  const websocketBidders = computed(() => {
    return (auctionStore.userOnLive || []).filter((user) => user.id !== auctionStore.user?.id);
  });

  const usersList = computed(() => {
    let result = [] as IUserListItem[];
    websocketBidders.value.forEach((user: IUserListItem) => {
      const uData = auctionStore.userData[user.id];
      if (uData) {
        if (user.status) {
          uData.status = user.status;
        } else {
          uData.status = "clarified";
        }
        result.push(uData);
      }
    });

    return result.sort((a: IUserListItem, b: IUserListItem) => a.full_name.localeCompare(b.full_name));
  });

  const activeCount = computed(() => {
    return usersList.value.filter((i) => i.status === "active").length;
  });

  const pendingUsers = computed(() => {
    return usersList.value.filter((i) => {
      return [CustomerStatus.NONE, CustomerStatus.PENDING].includes(i.customer_status);
    });
  });

  const filteredUsersList = computed(() => {
    let result = [] as IUserListItem[];
    if (!filter.value) {
      result = usersList.value;
    } else if (filter.value === "active") {
      result = usersList.value.filter((i) => i.status === "active");
    } else if (filter.value === "pending") {
      result = usersList.value.filter((i) => [CustomerStatus.NONE, CustomerStatus.PENDING].includes(i.customer_status));
    }

    return result;
  });

  watch(
    () => auctionStore.userOnLive,
    (nv) => {
      const userIdsNoData = [] as number[];

      if (!nv.length) {
        Object.keys(auctionStore.userData).forEach((id) => {
          auctionStore.userData[id].status = "clarified";
        });
      } else {
        nv.forEach((user: IUserListItem) => {
          if (!auctionStore.userData[user.id]) {
            userIdsNoData.push(user.id);
          } else {
            auctionStore.userData[user.id].status = user.status ? user.status : "clarified";
          }
        });
      }

      if (userIdsNoData?.length > 0) {
        apiClient
          .post(auctionStore.routes["live.users.get_users_data"], { userIds: userIdsNoData })
          .then((response) => {
            if (response.data.data?.length) {
              response.data.data.forEach((user: IUserListItem) => {
                auctionStore.userData[user.id] = user;
              });
            }
          });
      }
    },
    { deep: true }
  );

  const getUserStatus = (user: IUserListItem) => {
    let result;
    switch (user.customer_status) {
      case CustomerStatus.NONE:
      case CustomerStatus.PENDING:
        result = "pending";
        break;
      case CustomerStatus.BLOCKED:
        result = "blocked";
        break;
      default:
        result = "";
    }

    return result;
  };

  const openChat = (user: IUserListItem) => {
    emitter?.emit("on:user-message", user);
  };

  const openUserPopup = (user: IUserListItem) => {
    apiClient.post(auctionStore.routes["live.users.get_full_data"], { userId: user.id }).then((response) => {
      if (response.data.data.id) {
        popupTitle.value = user.full_name ?? null;
        popup.value?.toggleComponent(component, { data: { user: response.data.data } });
        nextTick(() => {
          popup.value?.open();
        });
      }
    });
  };
</script>
